import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, isObservable} from 'rxjs';
import {SmConfirmationDialogRequest, SmConfirmationDialogResult, SmConfirmationDialogState} from './sm-confirmation-dialog.models';
import {wasSystemApiErrorPresented} from '../../errors/local-api-error';

const INITIAL_STATE: SmConfirmationDialogState = {
  isOpen: false,
  title: '',
  description: '',
  bodyBlocks: [],
  confirmationText: '',
  confirmationPrompt: '',
  confirmationPlaceholder: '',
  confirmationMismatchLabel: '',
  textInputLabel: '',
  textInputPlaceholder: '',
  textInputInputMode: 'text',
  textInputRequired: false,
  textInputMultiline: false,
  cancelLabel: '',
  alternateLabel: '',
  confirmLabel: '',
  closeLabel: '',
  busy: false,
  error: '',
  dialogType: 'default',
  confirmTone: 'danger',
  contentAlign: 'center',
  actionsAlign: 'center',
  compactActions: false,
  panelVariant: 'default',
  expandToViewport: false,
  heroIconName: '',
  showCloseButton: false,
  showCancelButton: true,
  showAlternateButton: false,
  showConfirmButton: true,
};

@Injectable({
  providedIn: 'root',
})
export class SmConfirmationDialogService {
  private readonly stateSubject = new BehaviorSubject<SmConfirmationDialogState>(INITIAL_STATE);
  private currentRequest: SmConfirmationDialogRequest | null = null;
  private currentResolver: ((result: SmConfirmationDialogResult) => void) | null = null;

  readonly state$ = this.stateSubject.asObservable();

  open(request: SmConfirmationDialogRequest): Promise<boolean> {
    return this.openChoice(request).then((result) => result === 'confirm');
  }

  openChoice(request: SmConfirmationDialogRequest): Promise<SmConfirmationDialogResult> {
    this.resolveCurrent('cancel');
    this.currentRequest = request;
    const dialogType = request.dialogType ?? 'default';
    const contentAlign = request.contentAlign ?? 'center';
    this.stateSubject.next({
      isOpen: true,
      title: request.title,
      description: request.description ?? '',
      bodyBlocks: request.bodyBlocks ?? [],
      bodyTemplate: request.bodyTemplate,
      confirmationText: request.confirmationText ?? '',
      confirmationPrompt: request.confirmationPrompt ?? '',
      confirmationPlaceholder: request.confirmationPlaceholder ?? '',
      confirmationMismatchLabel: request.confirmationMismatchLabel ?? '',
      textInputLabel: request.textInputLabel ?? '',
      textInputPlaceholder: request.textInputPlaceholder ?? '',
      textInputInputMode: request.textInputInputMode ?? 'text',
      textInputRequired: request.textInputRequired ?? false,
      textInputMultiline: request.textInputMultiline ?? false,
      cancelLabel: request.cancelLabel,
      alternateLabel: request.alternateLabel ?? '',
      confirmLabel: request.confirmLabel,
      closeLabel: request.closeLabel,
      busy: false,
      error: '',
      dialogType,
      confirmTone: request.confirmTone ?? 'danger',
      contentAlign,
      actionsAlign: request.actionsAlign ?? (contentAlign === 'center' ? 'center' : 'end'),
      compactActions: request.compactActions ?? false,
      panelVariant: request.panelVariant ?? 'default',
      expandToViewport: request.expandToViewport ?? false,
      heroIconName: request.heroIconName ?? this.defaultIconName(dialogType),
      showCloseButton: request.showCloseButton ?? false,
      showCancelButton: request.showCancelButton ?? true,
      showAlternateButton: request.showAlternateButton ?? false,
      showConfirmButton: request.showConfirmButton ?? true,
    });

    return new Promise<SmConfirmationDialogResult>((resolve) => {
      this.currentResolver = resolve;
    });
  }

  cancel(): void {
    const request = this.currentRequest;
    const state = this.stateSubject.value;
    if (!request || state.busy) {
      return;
    }

    request.onCancel?.();
    this.resolveCurrent('cancel');
  }

  alternate(): void {
    if (!this.currentRequest || this.stateSubject.value.busy) {
      return;
    }

    this.resolveCurrent('alternate');
  }

  async confirm(textInputValue = ''): Promise<void> {
    const request = this.currentRequest;
    const state = this.stateSubject.value;
    if (!request || state.busy) {
      return;
    }

    this.stateSubject.next({
      ...state,
      busy: true,
      error: '',
    });

    try {
      const confirmResult = request.onConfirm?.(textInputValue);
      if (isObservable(confirmResult)) {
        await firstValueFrom(confirmResult);
      } else if (confirmResult instanceof Promise) {
        await confirmResult;
      }

      this.resolveCurrent('confirm');
    } catch (error) {
      this.stateSubject.next({
        ...this.stateSubject.value,
        busy: false,
        error: this.formatError(error, request),
      });
    }
  }

  private formatError(error: unknown, request: SmConfirmationDialogRequest): string {
    if (wasSystemApiErrorPresented(error)) return '';
    const formattedError = request.errorFormatter?.(error);
    if (typeof formattedError === 'string' && formattedError.trim().length > 0) {
      return formattedError.trim();
    }

    if (formattedError) {
      return String(formattedError);
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message.trim();
    }

    return request.errorFallback ?? 'Unexpected error';
  }

  private defaultIconName(dialogType: SmConfirmationDialogState['dialogType']): string {
    switch (dialogType) {
      case 'information':
        return 'information-circle-outline';
      case 'warning':
        return 'warning-outline';
      case 'danger':
        return 'alert-circle-outline';
      case 'default':
        return '';
    }
  }

  private resolveCurrent(result: SmConfirmationDialogResult): void {
    this.stateSubject.next(INITIAL_STATE);
    this.currentRequest = null;

    const resolver = this.currentResolver;
    this.currentResolver = null;
    resolver?.(result);
  }
}
